import dayjs, { Dayjs } from 'dayjs';
import { PolicyHeaderDateFormat } from '../_types/manuals/draft';

/**
 * Formats a preview date for demonstration purposes.
 * Used in UI previews to show how dates will appear.
 *
 * @param format - The format type ('MMYYYY' or 'MMDDYYYY')
 * @returns Formatted current date string for preview
 */
export const formatDatePreview = (format?: PolicyHeaderDateFormat): string => {
  const now = dayjs();
  if (format === 'MMYYYY') {
    return now.format('MMMM D, YYYY');
  }
  return now.format('MM/DD/YYYY');
};

/**
 * Formats a Dayjs date for saving to the backend based on the specified format.
 *
 * @param date - The Dayjs date object to format
 * @param dateFormat - The format type ('MMYYYY' or 'MMDDYYYY')
 * @returns Formatted date string, or undefined if date is null/undefined
 */
export const formatDateForSave = (date: Dayjs | null | undefined, dateFormat?: string): string | undefined => {
  if (!date) return undefined;

  return dateFormat === 'MMYYYY'
    ? date.format('MMMM D, YYYY')
    : date.format('MM/DD/YYYY');
};

/**
 * Parses a saved date string back into a Dayjs object.
 * Attempts multiple known formats to handle cases where the stored format
 * doesn't match the current config (e.g., value is "February 16, 2026" but format is MMDDYYYY).
 *
 * @param dateValue - The stored date string in any known format
 * @param _dateFormat - The format type (unused, kept for API compatibility)
 * @returns Dayjs object, or null if the value is empty or invalid
 */
export const formatDateForLoad = (dateValue: string, _dateFormat?: string): Dayjs | null => {
  if (!dateValue) return null;

  const knownFormats = ['MM/DD/YYYY', 'MMMM D, YYYY'];

  for (const format of knownFormats) {
    const date = dayjs(dateValue, format, true);
    if (date.isValid()) return date;
  }

  return null;
};
